import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { Layout } from '../components/layout';
import { ProductCard } from '../components/ProductCard';
import { RegionSwitcher } from '../components/RegionSwitcher';
import { Loading, Button, EmptyState, Modal, ImageWithLoader } from '../components/ui';
import { api } from '../services/api';
import { useUIStore, useCatalogStore, useSettingsStore, useUserStore } from '../store';
import { Product, Collection, FilterState, Platform, Language, ProductCategory, Region } from '../types';
import { formatPrice, formatDiscount, getEditionName, accountRate, splitByPlatform, languageKind } from '../utils/helpers';
import { useProductSearch } from '../hooks/useProductSearch';
import { optimizeImage } from '../utils/image';
import { SearchInput } from '../components/SearchInput';

const CATEGORIES: { value: ProductCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'game', label: 'Игры' },
  { value: 'donat', label: 'Дополнения' },
  { value: 'subscription', label: 'Подписки' },
];

const SUBSCRIPTION_COLLECTION = 'subscriptions';
const NEW_RELEASES_COLLECTION = 'new';

// In-memory кеши результатов поиска и категорий: чтобы при возврате с товара
// HomePage показывал данные мгновенно без повторного fetch (иначе scroll
// перескакивает наверх пока запрос идет).
const homeCategoryCache = new Map<string, { products: Product[]; total: number }>();
const homeServerSearchCache = new Map<string, Product[]>();

const ORDERED_COLLECTIONS = [
  'bestsellers',
  'preorders',
  'topup',
  'under-1000',
  'under-2000',
  'bundles',
  'topup-tr',
  'topup-in', 
  'topup-ua',
];

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const setSearchActive = useUIStore((s) => s.setSearchActive);
  const homeUserRegion = useUserStore((s) => s.lastSelectedRegion);
  const catalogStore = useCatalogStore();
  const [products, setProducts] = useState<Product[]>(catalogStore.products);
  const [collections, setCollections] = useState<Collection[]>(catalogStore.collections);
  const [banners, setBanners] = useState<Array<{ id: string; title: string; imageUrl: string; linkUrl: string | null; linkType: string }>>(catalogStore.banners);
  const [isLoading, setIsLoading] = useState(!catalogStore.loaded);

  const homeCategory = useUIStore((s) => s.homeCategory);
  const setHomeCategoryStore = useUIStore((s) => s.setHomeCategory);
  const homeFilters = useUIStore((s) => s.homeFilters);
  const setHomeFiltersStore = useUIStore((s) => s.setHomeFilters);
  const homeSearchQuery = useUIStore((s) => s.homeSearchQuery);
  const setHomeSearchQueryStore = useUIStore((s) => s.setHomeSearchQuery);

  const [debouncedQuery, setDebouncedQuery] = useState(homeSearchQuery);
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeCategory, setActiveCategoryLocal] = useState<ProductCategory | 'all'>(homeCategory);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFiltersLocal] = useState<FilterState>(homeFilters);
  const defaultFilters: FilterState = { platforms: [], priceRange: [0, 22400], languages: [], categories: [], regions: [] };
  const [draftFilters, setDraftFilters] = useState<FilterState>(homeFilters);

  const handleSearchQuery = useCallback((q: string) => {
    setDebouncedQuery(q);
    setHomeSearchQueryStore(q);
  }, [setHomeSearchQueryStore]);

  const handleSearchFocus = useCallback((focused: boolean) => {
    setSearchFocused(focused);
  }, []);

  const setActiveCategory = useCallback((cat: ProductCategory | 'all') => {
    setActiveCategoryLocal(cat);
    setHomeCategoryStore(cat);
  }, [setHomeCategoryStore]);

  const setFilters = useCallback((f: FilterState | ((prev: FilterState) => FilterState)) => {
    setFiltersLocal((prev) => {
      const next = typeof f === 'function' ? f(prev) : f;
      setHomeFiltersStore(next);
      return next;
    });
  }, [setHomeFiltersStore]);

  const categoryRef = useRef<HTMLDivElement>(null);

  const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);
  const [categoryTotal, setCategoryTotal] = useState(0);
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const loadingMoreCategoryRef = useRef(false);
  const categoryObserverRef = useRef<IntersectionObserver | null>(null);

  // Preload banner images so they're ready before carousel renders
  useEffect(() => {
    banners.forEach((b) => {
      const img = new Image();
      img.src = optimizeImage(b.imageUrl, 800);
    });
  }, [banners]);

  useEffect(() => {
    setSearchActive(searchFocused);
    return () => setSearchActive(false);
  }, [searchFocused, setSearchActive]);

  const isCatalogMode = debouncedQuery.trim() !== '' || activeCategory !== 'all' ||
    filters.platforms.length > 0 || filters.languages.length > 0 ||
    filters.regions.length > 0 || filters.priceRange[0] !== 0 || filters.priceRange[1] !== 22400;

  useEffect(() => {
    if (catalogStore.loaded && isLoading) {
      setProducts(catalogStore.products);
      setCollections(catalogStore.collections);
      setBanners(catalogStore.banners);
      setIsLoading(false);
    }
  }, [catalogStore.loaded, catalogStore.products, catalogStore.collections, catalogStore.banners, isLoading]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeCategory === 'all') {
      setCategoryProducts([]);
      setCategoryTotal(0);
      return;
    }
    let cancelled = false;

    // Если в кеше уже есть данные по этой категории — отдаем мгновенно,
    // fetch в фоне не запускаем (свежесть гарантируется Redis-кешом backend).
    const cached = homeCategoryCache.get(activeCategory);
    if (cached) {
      setCategoryProducts(cached.products);
      setCategoryTotal(cached.total);
      setIsLoadingCategory(false);
      return;
    }

    const load = async () => {
      setIsLoadingCategory(true);
      try {
        const res = await api.getProducts({ category: activeCategory, limit: 200, offset: 0 });
        if (!cancelled) {
          setCategoryProducts(res.products);
          setCategoryTotal(res.total);
          homeCategoryCache.set(activeCategory, { products: res.products, total: res.total });
        }
      } finally {
        if (!cancelled) setIsLoadingCategory(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeCategory]);

  const loadMoreCategory = useCallback(async () => {
    if (loadingMoreCategoryRef.current || activeCategory === 'all') return;
    if (categoryProducts.length >= categoryTotal) return;
    loadingMoreCategoryRef.current = true;
    try {
      const res = await api.getProducts({ category: activeCategory, limit: 200, offset: categoryProducts.length });
      if (res.products.length > 0) {
        setCategoryProducts(prev => [...prev, ...res.products]);
      }
      setCategoryTotal(res.total);
    } finally {
      loadingMoreCategoryRef.current = false;
    }
  }, [activeCategory, categoryProducts.length, categoryTotal]);

  const categorySentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (categoryObserverRef.current) categoryObserverRef.current.disconnect();
    if (!node) return;
    categoryObserverRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loadingMoreCategoryRef.current) loadMoreCategory();
    }, { rootMargin: '400px' });
    categoryObserverRef.current.observe(node);
  }, [loadMoreCategory]);

  const loadData = async () => {
    try {
      const [gamesRes, subsRes, collectionsData, bannersData] = await Promise.all([
        api.getProducts({ category: 'game', limit: 500 }),
        api.getProducts({ category: 'subscription', limit: 50 }),
        api.getCollections(),
        api.getBanners(),
      ]);
      setProducts([...gamesRes.products, ...subsRes.products]);
      setCollections(collectionsData);
      setBanners(bannersData);
    } finally {
      setIsLoading(false);
    }
  };

  const getProductsForCollection = (collection: Collection): Product[] => {
    return collection.productIds
      .map((id) => products.find((p) => p.id === id))
      .filter((p): p is Product => p !== undefined);
  };

  const allSearchable = useMemo(() => {
    if (activeCategory !== 'all' && categoryProducts.length > 0) {
      const merged = [...products];
      const ids = new Set(merged.map(p => p.id));
      for (const cp of categoryProducts) {
        if (!ids.has(cp.id)) merged.push(cp);
      }
      return merged;
    }
    return products;
  }, [products, categoryProducts, activeCategory]);

  const searchResults = useProductSearch(allSearchable, debouncedQuery);

  const [serverSearchResults, setServerSearchResults] = useState<Product[]>([]);
  const [isServerSearching, setIsServerSearching] = useState(false);
  const serverSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!debouncedQuery.trim() || debouncedQuery.trim().length < 2) {
      setServerSearchResults([]);
      setIsServerSearching(false);
      return;
    }

    // Если результат этого запроса уже есть в кеше — отдаем мгновенно,
    // не дергаем сервер. Это критично для back-навигации: иначе скролл
    // сдвигается пока 350мс debounce + API возвращают то же самое.
    const cacheKey = `${activeCategory}:${homeUserRegion || 'none'}:${debouncedQuery.trim()}`;
    const cached = homeServerSearchCache.get(cacheKey);
    if (cached) {
      setServerSearchResults(cached);
      setIsServerSearching(false);
      return;
    }

    setIsServerSearching(true);
    if (serverSearchTimerRef.current) clearTimeout(serverSearchTimerRef.current);
    serverSearchTimerRef.current = setTimeout(async () => {
      try {
        const results = await api.searchProducts(debouncedQuery.trim(), activeCategory, homeUserRegion || undefined);
        setServerSearchResults(results);
        homeServerSearchCache.set(cacheKey, results);
      } catch {
        setServerSearchResults([]);
      } finally {
        setIsServerSearching(false);
      }
    }, 350);
    return () => { if (serverSearchTimerRef.current) clearTimeout(serverSearchTimerRef.current); };
  }, [debouncedQuery, activeCategory, homeUserRegion]);

  const mergedSearchResults = useMemo(() => {
    if (!debouncedQuery.trim()) return [];
    const merged = [...searchResults];
    const ids = new Set(merged.map(p => p.id));
    for (const p of serverSearchResults) {
      if (!ids.has(p.id)) merged.push(p);
    }
    return merged;
  }, [searchResults, serverSearchResults, debouncedQuery]);

  const catalogResults = useMemo(() => {
    if (!isCatalogMode) return [];

    const hasQuery = debouncedQuery.trim() !== '';

    let filtered: Product[];

    if (hasQuery) {
      filtered = [...mergedSearchResults];
      if (activeCategory !== 'all') {
        filtered = filtered.filter((p) => p.category === activeCategory);
      }
    } else if (activeCategory !== 'all') {
      filtered = [...categoryProducts];
    } else {
      filtered = [...products];
    }

    if (filters.platforms.length > 0) {
      filtered = filtered.filter((p) =>
        p.variants.some((v) => v.platform && filters.platforms.includes(v.platform))
      );
    }

    const [minPrice, maxPrice] = filters.priceRange;
    filtered = filtered.filter((p) => p.minPrice >= minPrice && p.minPrice <= maxPrice);

    if (filters.languages.length > 0) {
      filtered = filtered.filter((p) =>
        p.variants.some((v) => { const k = languageKind(v.language); return !!k && filters.languages.includes(k); })
      );
    }

    if (filters.regions.length > 0) {
      filtered = filtered.filter((p) =>
        p.variants.some((v) => filters.regions.includes(v.region))
      );
    }

    if (hasQuery && activeCategory === 'all') {
      const categoryOrder: Record<string, number> = { game: 0, subscription: 1, topup: 2, donat: 3 };
      filtered.sort((a, b) => (categoryOrder[a.category] ?? 9) - (categoryOrder[b.category] ?? 9));
    }

    return filtered;
  }, [isCatalogMode, debouncedQuery, mergedSearchResults, products, categoryProducts, activeCategory, filters]);

  const activeFiltersCount =
    filters.platforms.length +
    filters.languages.length +
    filters.regions.length +
    (filters.priceRange[0] !== 0 || filters.priceRange[1] !== 22400 ? 1 : 0);

  const activeCategoryLabel = CATEGORIES.find(c => c.value === activeCategory)?.label || 'Все';

  const handleSelectCategory = (value: ProductCategory | 'all') => {
    setActiveCategory(value);
    setShowCategoryDropdown(false);
  };

  const togglePlatform = (platform: Platform) => {
    setDraftFilters((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const toggleLanguage = (language: Language) => {
    setDraftFilters((prev) => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter((l) => l !== language)
        : [...prev.languages, language],
    }));
  };

  const toggleRegion = (region: Region) => {
    setDraftFilters((prev) => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter((r) => r !== region)
        : [...prev.regions, region],
    }));
  };

  const handleApplyFilters = () => {
    setFilters(draftFilters);
    setShowFilters(false);
  };

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters });
    setShowFilters(true);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setDraftFilters(defaultFilters);
    handleSearchQuery('');
    setActiveCategory('all');
  };

  const handleResetInModal = () => {
    setDraftFilters(defaultFilters);
  };

  if (isLoading) {
    return <Layout><div /></Layout>;
  }

  const psPlusProducts = products.filter(p => p.id.startsWith('ps-plus-'));
  const eaPlayProducts = products.filter(p => p.id.startsWith('ea-play-'));

  const newReleasesCollection = collections.find(c => c.id === NEW_RELEASES_COLLECTION);
  const newReleaseProducts = newReleasesCollection
    ? getProductsForCollection(newReleasesCollection)
    : [];

  const getCollection = (id: string) => collections.find(c => c.id === id);

  return (
    <Layout>
      <div className="pb-2">
        {/* Search + Category + Filters — sticky bar */}
        <div className="sticky top-0 z-20 bg-white px-3 pt-2 pb-1 relative" style={{ transform: 'translateZ(0)' }}>
          <div className="flex items-center gap-1.5">
            <SearchInput
              initialValue={homeSearchQuery}
              onQueryChange={handleSearchQuery}
              onFocusChange={handleSearchFocus}
              focused={searchFocused}
              debounceMs={200}
            />

            {!searchFocused && (
              <div className="relative flex-shrink-0" ref={categoryRef}>
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  className={`flex items-center gap-1 px-2.5 py-2 rounded-[14px] text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory !== 'all'
                      ? 'bg-primary text-white'
                      : 'bg-background-secondary'
                  }`}
                  style={activeCategory === 'all' ? { color: '#1A1D1A' } : undefined}
                >
                  <span className="max-w-[60px] truncate">{activeCategoryLabel}</span>
                  <svg
                    className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${showCategoryDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showCategoryDropdown && (
                  <div className="absolute top-full right-0 mt-1.5 bg-white rounded-[14px] border border-gray-200/80 z-30 min-w-[160px] animate-fade-in overflow-hidden">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        onClick={() => handleSelectCategory(cat.value)}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          activeCategory === cat.value
                            ? 'text-primary font-semibold bg-primary-50'
                            : 'text-text-primary hover:bg-background-secondary'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!searchFocused && (
              <button
                onClick={handleOpenFilters}
                className="relative flex items-center justify-center transition-all flex-shrink-0"
                style={{
                  padding: '8px',
                  borderRadius: '14px',
                  background: activeFiltersCount > 0 ? 'rgba(54, 87, 240, 0.08)' : '#F4F4F7',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21.25 12H8.895M4.534 12H2.75M4.534 12C4.534 11.4218 4.76368 10.8673 5.17251 10.4585C5.58134 10.0497 6.13583 9.82 6.714 9.82C7.29217 9.82 7.84666 10.0497 8.25549 10.4585C8.66432 10.8673 8.894 11.4218 8.894 12C8.894 12.5782 8.66432 13.1327 8.25549 13.5415C7.84666 13.9503 7.29217 14.18 6.714 14.18C6.13583 14.18 5.58134 13.9503 5.17251 13.5415C4.76368 13.1327 4.534 12.5782 4.534 12ZM21.25 18.607H15.502M15.502 18.607C15.502 19.1853 15.2718 19.7404 14.8628 20.1493C14.4539 20.5583 13.8993 20.788 13.321 20.788C12.7428 20.788 12.1883 20.5573 11.7795 20.1485C11.3707 19.7397 11.141 19.1852 11.141 18.607M15.502 18.607C15.502 18.0287 15.2718 17.4746 14.8628 17.0656C14.4539 16.6567 13.8993 16.427 13.321 16.427C12.7428 16.427 12.1883 16.6567 11.7795 17.0655C11.3707 17.4743 11.141 18.0288 11.141 18.607M11.141 18.607H2.75M21.25 5.393H18.145M13.784 5.393H2.75M13.784 5.393C13.784 4.81483 14.0137 4.26033 14.4225 3.8515C14.8313 3.44268 15.3858 3.213 15.964 3.213C16.2503 3.213 16.5338 3.26938 16.7983 3.37894C17.0627 3.4885 17.3031 3.64907 17.5055 3.8515C17.7079 4.05394 17.8685 4.29426 17.9781 4.55875C18.0876 4.82324 18.144 5.10672 18.144 5.393C18.144 5.67928 18.0876 5.96276 17.9781 6.22725C17.8685 6.49174 17.7079 6.73206 17.5055 6.93449C17.3031 7.13692 17.0627 7.2975 16.7983 7.40705C16.5338 7.51661 16.2503 7.573 15.964 7.573C15.3858 7.573 14.8313 7.34332 14.4225 6.93449C14.0137 6.52566 13.784 5.97117 13.784 5.393Z" stroke="#1A1D1A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
                </svg>
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            )}

            {/* Регион каталога — выпадающий список, как на сайте */}
            {!searchFocused && <RegionSwitcher />}
          </div>
        </div>

        {isCatalogMode ? (
          <div className="px-4 pb-6 pt-3">
            {!isLoadingCategory && catalogResults.length > 0 && (
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-text-tertiary">
                  Найдено: {activeCategory !== 'all' && !debouncedQuery.trim()
                    ? categoryTotal
                    : debouncedQuery.trim()
                      ? catalogResults.reduce((n, p) => n + splitByPlatform(p, homeUserRegion).length, 0)
                      : catalogResults.length}
                </p>
                <button
                  onClick={resetFilters}
                  className="text-xs text-primary font-medium"
                >
                  Сбросить
                </button>
              </div>
            )}

            {catalogResults.length === 0 && (isLoadingCategory || (isServerSearching && catalogResults.length === 0)) ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square rounded-[14px] bg-gray-200 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : catalogResults.length === 0 && debouncedQuery.trim() ? (
              <EmptyState
                title="Ничего не найдено"
                description="Попробуйте изменить параметры поиска или фильтры"
                action={{ label: 'Сбросить фильтры', onClick: resetFilters }}
              />
            ) : catalogResults.length === 0 ? (
              <EmptyState
                title="Ничего не найдено"
                description="В этой категории пока нет товаров"
                action={{ label: 'Сбросить фильтры', onClick: resetFilters }}
              />
            ) : (
              <>
                {debouncedQuery.trim() ? (
                  (() => {
                    const games = catalogResults.filter((p) => p.category === 'game' || p.category === 'topup' || !p.category);
                    const donats = catalogResults.filter((p) => p.category === 'donat');
                    const subscriptions = catalogResults.filter((p) => p.category === 'subscription');
                    return (
                      <>
                        {games.length > 0 && (
                          <section className="mb-6">
                            <h2 className="mb-3" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '120%', letterSpacing: '-0.3px', color: '#1A1D1A' }}>
                              Игры
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                              {/* Версии «для PS4» и «для PS4/PS5» одной игры — отдельными плитками, как издания */}
                              {games.flatMap((p) => splitByPlatform(p, homeUserRegion)).map(({ product, variant }) => (
                                <ProductCard key={variant ? `${product.id}:${variant.id}` : product.id} product={product} variant={variant} orientation="vertical" />
                              ))}
                            </div>
                          </section>
                        )}
                        {donats.length > 0 && (
                          <section className="mb-6">
                            <h2 className="mb-3" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '120%', letterSpacing: '-0.3px', color: '#1A1D1A' }}>
                              Дополнения
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                              {donats.map((product) => (
                                <ProductCard key={product.id} product={product} orientation="vertical" />
                              ))}
                            </div>
                          </section>
                        )}
                        {subscriptions.length > 0 && (
                          <section>
                            <h2 className="mb-3" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '120%', letterSpacing: '-0.3px', color: '#1A1D1A' }}>
                              Подписки
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                              {subscriptions.map((product) => (
                                <ProductCard key={product.id} product={product} orientation="vertical" />
                              ))}
                            </div>
                          </section>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {catalogResults.map((product) => (
                      <ProductCard key={product.id} product={product} orientation="vertical" />
                    ))}
                  </div>
                )}
                {activeCategory !== 'all' && !debouncedQuery.trim() && categoryProducts.length < categoryTotal && (
                  <div ref={categorySentinelRef} className="flex justify-center py-6">
                    <svg className="animate-spin" width={28} height={28} viewBox="0 0 50 50" fill="none">
                      <circle cx="25" cy="25" r="20" stroke="#E5E7EB" strokeWidth="4" />
                      <path d="M25 5 A20 20 0 0 1 45 25" stroke="#4361EE" strokeWidth="4" strokeLinecap="round" />
                    </svg>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
        <>
        {/* ────── Home mode: collections ────── */}

        {/* Promo Banner Carousel */}
        {banners.length > 0 && (() => {
          const minSlides = 5;
          let loopBanners = [...banners];
          while (loopBanners.length < minSlides && banners.length > 1) {
            loopBanners = [...loopBanners, ...banners];
          }
          return (
          <div className="mb-2 mt-1 banner-carousel">
            <Swiper
              modules={[Autoplay, EffectCoverflow]}
              effect="coverflow"
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: false,
              }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              loop={banners.length > 1}
              spaceBetween={24}
              slidesPerView={1.5}
              centeredSlides={true}
              grabCursor={true}
              speed={500}
            >
              {loopBanners.map((banner, idx) => (
                <SwiperSlide key={`${banner.id}-${idx}`}>
                  <div
                    className="rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform aspect-[4/5] flex items-end relative"
                    onClick={() => {
                      if (banner.linkType === 'product' && banner.linkUrl) navigate(`/product/${banner.linkUrl}`);
                      else if (banner.linkType === 'collection' && banner.linkUrl) navigate(`/catalog/${banner.linkUrl}`);
                      else if (banner.linkType === 'url' && banner.linkUrl) window.open(banner.linkUrl, '_blank');
                      else navigate('/catalog');
                    }}
                  >
                    <ImageWithLoader
                      src={optimizeImage(banner.imageUrl, 800)}
                      alt={banner.title}
                      wrapperClassName="absolute inset-0 w-full h-full"
                      className="object-cover"
                      spinnerSize={28}
                      loading="eager"
                      decoding="sync"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          );
        })()}

        {/* 2. Лидеры продаж */}
        <ScrollableCollection collection={getCollection('bestsellers')} getProducts={getProductsForCollection} navigate={navigate} />

        {/* 3. Подписки PS Plus */}
        {psPlusProducts.length > 0 && (
          <PSPlusBlock products={psPlusProducts} onProductClick={(id) => navigate(`/product/${id}`)} />
        )}

        {/* 4. Подписки EA Play */}
        {eaPlayProducts.length > 0 && (
          <EAPlayBlock products={eaPlayProducts} onProductClick={(id) => navigate(`/product/${id}`)} />
        )}

        {/* 5. Новинки */}
        {newReleaseProducts.length > 0 && (
          <NewReleasesBlock products={newReleaseProducts} onProductClick={(id) => navigate(`/product/${id}`)} />
        )}

        {/* Остальные подборки по порядку */}
        {ORDERED_COLLECTIONS.slice(1).map((colId) => {
          if (colId === 'topup') {
            return <DonatGameCollection key={colId} collection={getCollection(colId)} getProducts={getProductsForCollection} navigate={navigate} />;
          }
          return <ScrollableCollection key={colId} collection={getCollection(colId)} getProducts={getProductsForCollection} navigate={navigate} />;
        })}
        </>
        )}
      </div>

      {/* Filters Modal */}
      <Modal
        isOpen={showFilters}
        onClose={() => { setDraftFilters({ ...filters }); setShowFilters(false); }}
        title="Фильтры"
        dismissable={false}
        footer={
          <div className="space-y-1.5">
            <Button variant="primary" size="sm" fullWidth onClick={handleApplyFilters}>Применить</Button>
            <Button variant="secondary" size="sm" fullWidth onClick={handleResetInModal}>Очистить все фильтры</Button>
          </div>
        }
      >
        <div className="space-y-3.5">
          {/* Platform */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-2">Платформа</h3>
            <div className="grid grid-cols-2 gap-2">
              {(['PS4', 'PS5'] as Platform[]).map((platform) => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium text-center ${
                    draftFilters.platforms.includes(platform)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200'
                  }`}
                  style={!draftFilters.platforms.includes(platform) ? { color: '#1A1D1A' } : undefined}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-0.5">Цена</h3>
            <p className="text-sm mb-1.5" style={{ color: '#1A1D1A' }}>
              {draftFilters.priceRange[0]} ₽ – {draftFilters.priceRange[1]} ₽
            </p>
            <input
              type="range"
              min="0"
              max="22400"
              step="100"
              value={draftFilters.priceRange[1]}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, priceRange: [0, parseInt(e.target.value)] }))
              }
              className="w-full accent-primary"
            />
          </div>

          {/* Language */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-2">Язык</h3>
            <div className="flex flex-col gap-1.5">
              {[
                { value: 'ru_subtitles' as Language, label: 'Русские субтитры' },
                { value: 'ru_full' as Language, label: 'Полностью на русском' },
              ].map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => toggleLanguage(lang.value)}
                  className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium text-left ${
                    draftFilters.languages.includes(lang.value)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200'
                  }`}
                  style={!draftFilters.languages.includes(lang.value) ? { color: '#1A1D1A' } : undefined}
                >
                  <span className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 ${
                    draftFilters.languages.includes(lang.value)
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {draftFilters.languages.includes(lang.value) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Region */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-2">Регион</h3>
            <div className="grid grid-cols-3 gap-2">
              {(['TR', 'IN', 'UA'] as Region[]).map((region) => (
                <button
                  key={region}
                  onClick={() => toggleRegion(region)}
                  className={`py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium text-center ${
                    draftFilters.regions.includes(region)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200'
                  }`}
                  style={!draftFilters.regions.includes(region) ? { color: '#1A1D1A' } : undefined}
                >
                  {region}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

/* ─── Collection Icon ──────────────────────────────────────────── */

const CollectionIcon: React.FC<{ collectionId: string }> = () => null;

/* ─── Donat Game Collection (grouped by game) ──────────────────── */

const DonatGameCollection: React.FC<{
  collection: Collection | undefined;
  getProducts: (c: Collection) => Product[];
  navigate: (path: string) => void;
}> = ({ collection, getProducts, navigate }) => {
  if (!collection) return null;
  const collectionProducts = getProducts(collection);
  if (collectionProducts.length === 0) return null;

  const gameMap = new Map<string, Product>();
  for (const p of collectionProducts) {
    const key = p.subtitle || p.name;
    if (!gameMap.has(key)) {
      gameMap.set(key, p);
    }
  }
  const uniqueGames = Array.from(gameMap.values());

  return (
    <div className="bg-white py-5 my-5 first:mt-0 last:mb-0 rounded-2xl">
      <div className="px-4 mb-3 flex items-center gap-2">
        <CollectionIcon collectionId={collection.id} />
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>{collection.title}</h2>
      </div>
      <div data-scroll-key={`carousel:${collection.id}`} className="flex gap-3 overflow-x-auto pb-2 px-4 scrollbar-hide">
        {uniqueGames.map((game) => {
          const displayName = game.subtitle || game.name;
          return (
            <div
              key={game.id}
              className="flex-shrink-0 w-[calc(50%-6px)] cursor-pointer active:scale-[0.97] transition-transform"
              onClick={() => navigate(`/donats/${encodeURIComponent(displayName)}`)}
            >
              <ImageWithLoader
                src={optimizeImage(game.coverImage, 400)}
                alt={displayName}
                wrapperClassName="w-full aspect-square rounded-[14px] mb-2.5"
                className="object-cover"
                spinnerSize={24}
                fallbackSrc="https://via.placeholder.com/200x260/e2e8f0/94a3b8?text=PS"
              />
              <p className="truncate mb-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
                {displayName}
              </p>
              <p className="truncate" style={{ fontSize: '13px', fontWeight: 400, lineHeight: '16px', letterSpacing: '-0.08px', color: '#818384' }}>
                Дополнения
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Scrollable Collection ─ */

const ScrollableCollection: React.FC<{
  collection: Collection | undefined;
  getProducts: (c: Collection) => Product[];
  navigate: (path: string) => void;
}> = ({ collection, getProducts, navigate }) => {
  if (!collection) return null;
  const collectionProducts = getProducts(collection);
  if (collectionProducts.length === 0) return null;

  return (
    <div className="bg-white py-5 my-5 first:mt-0 last:mb-0 rounded-2xl">
      <div className="px-4 mb-3 flex items-center gap-2">
        <CollectionIcon collectionId={collection.id} />
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>{collection.title}</h2>
      </div>
      <div data-scroll-key={`carousel:${collection.id}`} className="flex gap-3 overflow-x-auto pb-2 px-4 scrollbar-hide">
        {collectionProducts.map((product) => (
          <div key={product.id} className="flex-shrink-0 w-[calc(50%-6px)]">
            <CollectionProductCard product={product} onClick={() => navigate(`/product/${product.id}`)} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Collection Product Card (2-col grid) ───────────────────────── */

interface CollectionProductCardProps {
  product: Product;
  onClick: () => void;
}

const CollectionProductCard: React.FC<CollectionProductCardProps> = ({
  product,
  onClick,
}) => {
  const { settings } = useSettingsStore();
  const userRegion = useUserStore((s) => s.lastSelectedRegion);

  const { displayPrice, displayDiscount } = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return { displayPrice: product.minPrice, displayDiscount: null as number | null };
    }
    const computePrice = (v: typeof product.variants[number]) => {
      const rate = accountRate(v.region, v.regionalPrice || 0, product.rateOverrides, settings);
      return (rate && v.regionalPrice)
        ? Math.ceil((v.regionalPrice * rate) / 5) * 5
        : v.price;
    };
    const pickCheapest = (vs: typeof product.variants) => {
      const withPrice = vs.map(v => ({ v, p: computePrice(v) })).filter(x => x.p > 0);
      if (!withPrice.length) return null;
      return withPrice.reduce((min, cur) => cur.p < min.p ? cur : min);
    };
    if (userRegion) {
      // Регион пользователя показываем ВСЕГДА (как на странице товара), не завязываясь на
      // флаг availableRegions — иначе цена в карточке и на странице товара расходятся.
      const regional = product.variants
        .filter(v => v.available && v.region === userRegion);
      const best = pickCheapest(regional);
      if (best) return { displayPrice: best.p, displayDiscount: best.v.discount ?? null };
    }
    const all = product.variants
      .filter(v => v.available && settings.availableRegions[v.region as Region]);
    const best = pickCheapest(all);
    if (best) return { displayPrice: best.p, displayDiscount: best.v.discount ?? null };
    return { displayPrice: product.minPrice, displayDiscount: null };
  }, [product, settings.exchangeRates, settings.rangePricing, settings.availableRegions, userRegion]);

  const cardSubtitle = useMemo(() => {
    if (product.category === 'donat') return 'Дополнение';
    if (product.category === 'game') {
      const edition = product.variants?.find(v => v.edition)?.edition;
      if (edition) return getEditionName(edition);
    }
    return product.subtitle || null;
  }, [product]);

  return (
    <div
      className="cursor-pointer active:scale-[0.97] transition-transform"
      onClick={onClick}
    >
      <ImageWithLoader
        src={optimizeImage(product.coverImage, 400)}
        alt={product.name}
        wrapperClassName="w-full aspect-square rounded-[14px] mb-2.5"
        className="object-cover"
        spinnerSize={24}
        fallbackSrc="https://via.placeholder.com/200x260/e2e8f0/94a3b8?text=PS"
      />
      <p className="truncate mb-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
        {product.name}
      </p>
      {cardSubtitle && (
        <p className="truncate" style={{ fontSize: '13px', fontWeight: 400, lineHeight: '16px', letterSpacing: '-0.08px', color: '#818384' }}>
          {cardSubtitle}
        </p>
      )}
      <div className="flex items-center gap-1.5 mt-0.5">
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', color: '#1A1D1A' }}>
          {formatPrice(displayPrice)}
        </p>
        {displayDiscount ? (
          <span className="bg-brand-gradient text-white text-[13px] font-bold px-1.5 py-0.5 rounded-md">
            -{displayDiscount}%
          </span>
        ) : null}
      </div>
    </div>
  );
};

/* ─── Subscription Card (shared) ─────────────────────────────────── */

const SubscriptionCard: React.FC<{
  product: Product;
  onClick: () => void;
}> = ({ product, onClick }) => {
  const { settings } = useSettingsStore();
  const userRegion = useUserStore((s) => s.lastSelectedRegion);
  // Extract tier name (e.g. "Essential" from "PS Plus Essential")
  const tierName = product.name.replace('PS Plus ', '').replace('EA Play', 'EA Play');

  const { displayPrice, displayDiscount } = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return { displayPrice: product.minPrice, displayDiscount: null as number | null };
    }
    const computePrice = (v: typeof product.variants[number]) => {
      const rate = accountRate(v.region, v.regionalPrice || 0, product.rateOverrides, settings);
      return (rate && v.regionalPrice)
        ? Math.ceil((v.regionalPrice * rate) / 5) * 5
        : v.price;
    };
    const pickCheapest = (vs: typeof product.variants) => {
      const withPrice = vs.map(v => ({ v, p: computePrice(v) })).filter(x => x.p > 0);
      if (!withPrice.length) return null;
      return withPrice.reduce((min, cur) => cur.p < min.p ? cur : min);
    };
    if (userRegion) {
      // Регион пользователя показываем ВСЕГДА (как на странице товара), не завязываясь на
      // флаг availableRegions — иначе цена в карточке и на странице товара расходятся.
      const regional = product.variants
        .filter(v => v.available && v.region === userRegion);
      const best = pickCheapest(regional);
      if (best) return { displayPrice: best.p, displayDiscount: best.v.discount ?? null };
    }
    const all = product.variants
      .filter(v => v.available && settings.availableRegions[v.region as Region]);
    const best = pickCheapest(all);
    if (best) return { displayPrice: best.p, displayDiscount: best.v.discount ?? null };
    return { displayPrice: product.minPrice, displayDiscount: null };
  }, [product, settings.exchangeRates, settings.rangePricing, settings.availableRegions, userRegion]);

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3.5 p-3 rounded-xl border border-gray-200/80 bg-white active:bg-gray-50 transition-colors text-left"
    >
      <img
        src={optimizeImage(product.coverImage, 200)}
        alt={product.name}
        className="w-24 h-24 rounded-xl object-cover flex-shrink-0 ring-1 ring-gray-300"
        onError={(e) => {
          (e.target as HTMLImageElement).src =
            'https://via.placeholder.com/96x96/e2e8f0/94a3b8?text=PS';
        }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold text-text-primary">{tierName}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <p className="text-xl font-bold text-text-primary">
            {formatPrice(displayPrice)}
          </p>
          {displayDiscount ? (
            <span className="bg-brand-gradient text-white text-[13px] font-bold px-1.5 py-0.5 rounded-md">
              {formatDiscount(displayDiscount)}
            </span>
          ) : null}
        </div>
      </div>
      <svg className="w-4 h-4 text-text-tertiary flex-shrink-0 -rotate-45" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </button>
  );
};

/* ─── PS Plus Block ──────────────────────────────────────────────── */

const PS_PLUS_PERIODS = [
  { label: '1 месяц', suffix: '1m' },
  { label: '3 месяца', suffix: '3m' },
  { label: '12 месяцев', suffix: '12m' },
];

const PSPlusBlock: React.FC<{
  products: Product[];
  onProductClick: (id: string) => void;
}> = ({ products, onProductClick }) => {
  // По умолчанию годовая подписка — ее покупают чаще всего
  const [activePeriod, setActivePeriod] = useState('12m');

  const TIER_ORDER: Record<string, number> = { essential: 0, extra: 1, deluxe: 2 };
  const filteredProducts = products
    .filter((p) => p.id.endsWith(`-${activePeriod}`))
    .sort((a, b) => {
      const tierA = a.id.replace('ps-plus-', '').replace(`-${activePeriod}`, '');
      const tierB = b.id.replace('ps-plus-', '').replace(`-${activePeriod}`, '');
      return (TIER_ORDER[tierA] ?? 99) - (TIER_ORDER[tierB] ?? 99);
    });

  return (
    <div className="bg-white py-5 px-4 my-5 rounded-2xl">
      <div className="flex items-center gap-2 mb-3">
        <CollectionIcon collectionId="ps-plus" />
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>Подписки PS Plus</h2>
      </div>

      {/* Period tabs — full width */}
      <div className="flex gap-2 mb-4">
        {PS_PLUS_PERIODS.map((period) => {
          const isActive = activePeriod === period.suffix;
          return (
            <button
              key={period.suffix}
              onClick={() => setActivePeriod(period.suffix)}
              className="flex-1 min-w-0 font-semibold transition-all"
              style={{
                padding: '10px 0',
                borderRadius: '14px',
                fontSize: '15px',
                ...(isActive
                  ? { background: 'linear-gradient(135deg, #5370F5, #3657F0)', color: '#fff' }
                  : { background: '#E0E4F9', color: '#3657F0' }),
              }}
            >
              {period.label}
            </button>
          );
        })}
      </div>

      {/* Tier cards */}
      <div className="space-y-2.5">
        {filteredProducts.map((product) => (
          <SubscriptionCard
            key={product.id}
            product={product}
            onClick={() => onProductClick(product.id)}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── EA Play Block ──────────────────────────────────────────────── */

const EAPlayBlock: React.FC<{
  products: Product[];
  onProductClick: (id: string) => void;
}> = ({ products, onProductClick }) => {
  return (
    <div className="bg-white py-5 px-4 my-5 rounded-2xl">
      <div className="flex items-center gap-2 mb-3">
        <CollectionIcon collectionId="ea-play" />
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>Подписки EA Play</h2>
      </div>

      <div className="space-y-2.5">
        {[...products].sort((a, b) => a.minPrice - b.minPrice).map((product) => (
          <SubscriptionCard
            key={product.id}
            product={product}
            onClick={() => onProductClick(product.id)}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── New Releases Block ──────────────────────────────────────────── */

interface NewReleasesBlockProps {
  products: Product[];
  onProductClick: (id: string) => void;
}

const NewReleasesBlock: React.FC<NewReleasesBlockProps> = ({
  products,
  onProductClick,
}) => {
  const displayed = products.slice(0, 15);

  return (
    <div className="bg-white py-5 my-5 rounded-2xl">
      <div className="px-4 mb-3 flex items-center gap-2">
        <CollectionIcon collectionId="new" />
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', lineHeight: '120%', letterSpacing: '-0.4px', color: '#1A1D1A' }}>Новинки</h2>
      </div>
      <div data-scroll-key="carousel:new" className="flex gap-3 overflow-x-auto pb-2 px-4 scrollbar-hide">
        {displayed.map((product) => (
          <div key={product.id} className="flex-shrink-0 w-[calc(50%-6px)]">
            <CollectionProductCard
              product={product}
              onClick={() => onProductClick(product.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
